import { FC, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation, Link as RouterLink } from 'react-router';
import { Box, Button, Divider, Grid, IconButton, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { ArrowBack, Favorite } from '@mui/icons-material';
import { marked } from 'marked';

import { useRouter } from '@cvt/routing';
import ErrorBoundary from '@cvt/components/ErrorBoundary';
import { PermissionedView } from '@cvt/components/Permissioned';
import { ListLoading } from '@cvt/components/layout/ListLoading';

import { MajorsList } from '@modules/Majors/partials/MajorsList';
import { InfiniteMajorsList } from '@modules/Majors/partials/InfiniteMajorsList';
import { MajorRow, MajorRowSkeleton } from '@modules/Majors/components/MajorRow';
import { InfiniteAssertionsList } from '@modules/Assertions/partials/InfiniteAssertionsList';
import { AnsweredAssertionRow, AnsweredAssertionRowSkeleton } from '@modules/Assertions/components/AnsweredAssertionRow';

import { formatPercentage } from '@shared/helpers/number';
import { ViewHeader } from '@shared/components/ViewHeader';
import { VideoCoverSection } from '@shared/components/VideoCoverSection';

import { useDiscipline } from '../../hooks/useDiscipline';
import { useDisciplineCrud } from '../../hooks/useDisciplineCrud';

interface ViewProps extends Disciplines.Discipline {
}

export const ViewDisciplineView: FC<ViewProps> = discipline => {
  const router = useRouter();
  const location = useLocation();

  const { likeDiscipline, unlikeDiscipline } = useDisciplineCrud();

  const actionsDisabled = [likeDiscipline.isPending, unlikeDiscipline.isPending].some(value => value);

  return (
    <Stack direction="column">
      <Box position="relative" width="100%" overflow="hidden">
        <VideoCoverSection image={discipline.image} video={discipline.video} alt={discipline.title} />
      </Box>
      <Routes>
        <Route path="/" element={(
          <Grid container spacing={2}>
            <Grid size={12}>
              <ViewHeader
                category="Discipline"
                title={discipline.title}
                slots={{
                  left: (
                    <IconButton component={RouterLink} to={router.disciplines.path} color="inherit">
                      <ArrowBack/>
                    </IconButton>
                  ),
                  right: (
                    <Box>
                      {discipline.match.isFavourite ? (
                        <IconButton onClick={() => unlikeDiscipline.mutateAsync(discipline.id)} disabled={actionsDisabled} color="error">
                          <Favorite />
                        </IconButton>
                      ) : (
                        <IconButton onClick={() => likeDiscipline.mutateAsync(discipline.id)} disabled={actionsDisabled}>
                          <Favorite />
                        </IconButton>
                      )}
                    </Box>
                  ),
                }}
              />
              <Box px={2} mb={4}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} pb={1}>
                  <Typography variant="body2">{formatPercentage(discipline.match.value)} Match</Typography>
                  <Button component={RouterLink} to={router.disciplines.view(discipline.id).assertions.path} color="primary" size="small" variant="text">
                      See Answers
                  </Button>
                </Stack>
                <LinearProgress
                  value={discipline.match.value * 100}
                  variant="determinate"
                  sx={{
                    height: 10,
                    borderRadius: 1,
                  }}
                />
              </Box>
              {discipline.description && (
                <Box px={2} mb={3}>
                  <Typography variant="body1" dangerouslySetInnerHTML={{ __html: marked(discipline.description) }} />
                </Box>
              )}
              {/* MAJORS OFFERED */}
              <Box
                p={2}
                width="100%"
                sx={theme => ({ backgroundColor: theme.palette.tertiary.light })}
              >
                <Typography variant="subtitle1" mb={0.5}>Majors offered</Typography>
                <PermissionedView permission="majors.list">
                  <Paper variant="outlined">
                    <Suspense key={location.key} fallback={(
                      <ListLoading
                        disablePadding
                        repeat={3}
                        renderItem={() => <MajorRowSkeleton/>}
                      />
                    )}>
                      <MajorsList
                        filters={{
                          discipline: discipline.id,
                          limit: 3,
                        }}
                        renderItem={major => <MajorRow key={major.id} {...major} />}
                        options={{
                          enablePagination: false,
                        }}
                      />
                    </Suspense>
                    <Divider/>
                    <Button
                      fullWidth
                      component={RouterLink}
                      to={router.disciplines.view(discipline.id).majors.path}
                    >
                      View all
                    </Button>
                  </Paper>
                </PermissionedView>
              </Box>
            </Grid>
          </Grid>
        )}/>
        <Route
          path="/assertions"
          element={(
            <>
              <ViewHeader
                category="Discipline"
                title={discipline.title}
                slots={{
                  left: (
                    <IconButton component={RouterLink} to={router.disciplines.view(discipline.id).path} color="primary">
                      <ArrowBack/>
                    </IconButton>
                  ),
                }}
              />
              <Box px={2}>
                <Typography variant="subtitle1" mb={0.5}>Answered assertions</Typography>
              </Box>
              <Box>
                <ErrorBoundary>
                  <PermissionedView permission="assertions.list">
                    <Suspense key={location.pathname} fallback={(
                      <ListLoading
                        repeat={3}
                        renderItem={() => <AnsweredAssertionRowSkeleton/>}
                      />
                    )}>
                      <InfiniteAssertionsList
                        filters={{
                          discipline: discipline.id,
                          answered: true,
                        }}
                        renderItem={university => <AnsweredAssertionRow key={university.id} {...university} />}
                        renderSkeleton={() => <AnsweredAssertionRowSkeleton/>}
                      />
                    </Suspense>
                  </PermissionedView>
                </ErrorBoundary>
              </Box>
            </>
          )}
        />
        <Route
          path="/majors"
          element={(
            <Grid container spacing={2}>
              <Grid size={12}>
                <ViewHeader
                  category="Discipline"
                  title={discipline.title}
                  slots={{
                    left: (
                      <IconButton component={RouterLink} to={router.disciplines.view(discipline.id).path} color="primary">
                        <ArrowBack/>
                      </IconButton>
                    ),
                  }}
                />
                <Box px={2}>
                  <Typography variant="subtitle1" mb={0.5}>Majors</Typography>
                </Box>
                <Box>
                  <ErrorBoundary>
                    <PermissionedView permission="majors.list">
                      <Suspense key={location.pathname} fallback={(
                        <ListLoading
                          repeat={3}
                          renderItem={() => <MajorRowSkeleton/>}
                        />
                      )}>
                        <InfiniteMajorsList
                          filters={{
                            discipline: discipline.id,
                          }}
                          renderItem={major => <MajorRow key={major.id} {...major} />}
                          renderSkeleton={() => <MajorRowSkeleton/>}
                        />
                      </Suspense>
                    </PermissionedView>
                  </ErrorBoundary>
                </Box>
              </Grid>
            </Grid>
          )}
        />
        <Route path="*" element={<Navigate to={router.disciplines.view(discipline.id).path} replace />}/>
      </Routes>
    </Stack>
  );
};

interface Props {
  id: number;
}

export const ViewDiscipline: FC<Props> = ({ id }) => {

  const { discipline } = useDiscipline({
    id,
  });

  if (!discipline) {
    return null;
  }

  return (
    <ViewDisciplineView {...discipline} />
  );
};
